import React from "react";

import {
  faFlask,
  faPlus,
  faWavePulse,
} from "@fortawesome/pro-regular-svg-icons";
import { useNavigate } from "react-router-dom";

import { DocumentTitle } from "src/base-components/DocumentTitle";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import {
  MENU_DIVIDER,
  OptionsDropdownElement,
} from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { FeatureCatalogueWrapper } from "src/featureCatalogue/FeatureCatalogue";
import { Queries } from "src/featureCatalogue/Queries/Queries";
import { useStatusToggle } from "src/featureCatalogue/useStatusToggle";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useEnvironment } from "src/hooks/useEnvironment";
import {
  DashboardPageParams,
  getUrlToManageFeatureQueryPage,
} from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

const QueriesHeader: React.FC = () => {
  const [environment, setEnvironment] = useEnvironment();
  const { featureQueriesCatalogue } = useCapabilities();
  const navigate = useNavigate();
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();

  const dropdownItems: OptionsDropdownElement[] = [
    {
      key: "Create Query",
      icon: faPlus,
      action: () => navigate(getUrlToManageFeatureQueryPage(orgId, wsId)),
      disabled: !featureQueriesCatalogue.canCreateQuery
        ? "You don't have permission to create queries"
        : undefined,
    },
    MENU_DIVIDER,
    {
      key: `Switch to ${environment === "sandbox" ? "Live" : "Sandbox"}`,
      icon: environment === "sandbox" ? faWavePulse : faFlask,
      action: () =>
        setEnvironment(environment === "sandbox" ? "live" : "sandbox"),
    },
  ] as const;

  return (
    <span className="border-gray-200 border-l p-3.5">
      <EllipsisOptionsDropdown
        dataLoc="queries-menu-dropdown"
        elements={dropdownItems}
        iconSize="sm"
        placement="bottom start"
      />
    </span>
  );
};

// I have kept the document title as "Features" by design. Currently, we don't show the Queries tab to our users; its only for CS.
export const QueriesPage: React.FC = () => {
  const { handleRequestStatusChange, statusToggleModal } = useStatusToggle();

  return (
    <FeatureCatalogueWrapper subHeaderRightContent={<QueriesHeader />}>
      <DocumentTitle segments={["Features"]} />
      <Queries onRequestStatusChange={handleRequestStatusChange} />
      {statusToggleModal}
    </FeatureCatalogueWrapper>
  );
};
